import type { RoomDistributionBet } from '../room/api';
import type { SocialConfig } from './heatConfig';

// The archetypes worth surfacing in a busy room (per product decision):
//   🔥 hot  — someone on a winning streak / high short-window win rate; players
//            crowd in to "catch the luck". Backend-computed (cross-round).
//   🐋 whale — a stake far above the room norm; pure spectacle, win or lose.
//            Derivable client-side from the round's own bets.
//   🧊 cold  — the mirror of hot: a losing streak / high loss rate. Surfaced so
//            players can "fade" them (bet the opposite side). Backend-computed.
export type HeatKind = 'hot' | 'whale' | 'cold';

export type HeatEntry = {
  bet: RoomDistributionBet;
  kind: HeatKind;
  stakeMultiple?: number; // whale: stake ÷ room median
  streak?: number; // hot: consecutive wins
  windowWinRate?: number; // hot: 0..1 over the short window
  lossStreak?: number; // cold: consecutive losses
  windowLossRate?: number; // cold: 0..1 over the short window
};

function betAmount(bet: RoomDistributionBet): number {
  return Number(bet.amount || 0);
}

function isBrl(bet: RoomDistributionBet): boolean {
  return String(bet.currency || 'BRL').toUpperCase() === 'BRL';
}

// Median stake of the round, so "big" is relative to THIS room's norm — a
// R$1000 bet is a whale in a R$10 room but ordinary in a high-roller one.
export function roomMedianStake(bets: RoomDistributionBet[]): number {
  const amounts = bets.map(betAmount).filter((n) => n > 0).sort((a, b) => a - b);
  if (amounts.length === 0) return 0;
  const mid = Math.floor(amounts.length / 2);
  return amounts.length % 2 ? amounts[mid] : (amounts[mid - 1] + amounts[mid]) / 2;
}

export function whaleStakeMultiple(bet: RoomDistributionBet, median: number): number {
  const explicit = Number(bet.stakeMultiple ?? bet.stake_multiple ?? 0);
  if (explicit > 0) return explicit;
  if (median <= 0) return 0;
  return betAmount(bet) / median;
}

export function isWhaleBet(bet: RoomDistributionBet, config: SocialConfig, median: number): boolean {
  if (bet.isWhale) return true;
  const amount = betAmount(bet);
  if (amount <= 0) return false;
  const floor = isBrl(bet) ? config.whale.minAbsoluteBrl : config.whale.minAbsoluteOther;
  if (amount >= floor) return true;
  return median > 0 && amount >= median * config.whale.minMultipleOfRoomMedian;
}

function hotStreak(bet: RoomDistributionBet): number {
  return Number(bet.user?.streak ?? bet.user?.winStreak ?? bet.user?.win_streak ?? 0) || 0;
}

function hotWindowWinRate(bet: RoomDistributionBet): number {
  return Number(bet.user?.windowWinRate ?? bet.user?.window_win_rate ?? 0) || 0;
}

// Hot needs cross-round history the client doesn't have, so it relies on backend
// heat fields. Until they arrive this returns false everywhere and the lane is
// simply empty — never fabricated.
export function isHotUser(bet: RoomDistributionBet, config: SocialConfig): boolean {
  if (bet.user?.hot) return true;
  if (hotStreak(bet) >= config.hot.minStreak) return true;
  return hotWindowWinRate(bet) >= config.hot.minWindowWinRate;
}

function coldLossStreak(bet: RoomDistributionBet): number {
  return Number(bet.user?.lossStreak ?? bet.user?.loss_streak ?? 0) || 0;
}

function coldWindowLossRate(bet: RoomDistributionBet): number {
  return Number(bet.user?.windowLossRate ?? bet.user?.window_loss_rate ?? 0) || 0;
}

// Cold mirrors hot and relies on the same backend heat fields; empty until they
// arrive. A user is never both hot and cold (win streak vs loss streak are
// mutually exclusive by construction).
export function isColdUser(bet: RoomDistributionBet, config: SocialConfig): boolean {
  if (bet.user?.cold) return true;
  if (coldLossStreak(bet) >= config.cold.minLossStreak) return true;
  return coldWindowLossRate(bet) >= config.cold.minWindowLossRate;
}

function heatUserId(bet: RoomDistributionBet): string {
  const user = bet.user || {};
  const raw = user.id ?? user.user_id ?? user.userId ?? user.player_id ?? user.playerId ?? user.username ?? '';
  return String(raw).trim();
}

// Curate the round's bets into two short, deduped, ranked lanes for the room
// heat rail. One entry per user (their most relevant bet), so 50 bettors
// collapse into "the few worth watching".
export function selectHeatHighlights(
  bets: RoomDistributionBet[],
  config: SocialConfig,
  limitPerLane = 4,
): { hot: HeatEntry[]; whales: HeatEntry[]; cold: HeatEntry[] } {
  const median = roomMedianStake(bets);
  const hotByUser = new Map<string, HeatEntry>();
  const whaleByUser = new Map<string, HeatEntry>();
  const coldByUser = new Map<string, HeatEntry>();

  for (const bet of bets) {
    if (bet.mine) continue; // never surface the viewer to themselves
    const id = heatUserId(bet);
    if (!id) continue;

    if (isHotUser(bet, config)) {
      const entry: HeatEntry = { bet, kind: 'hot', streak: hotStreak(bet), windowWinRate: hotWindowWinRate(bet) };
      const prev = hotByUser.get(id);
      if (!prev || (entry.streak || 0) > (prev.streak || 0)) hotByUser.set(id, entry);
    } else if (isColdUser(bet, config)) {
      // else-if: a user on a win streak can't also be cold this round.
      const entry: HeatEntry = { bet, kind: 'cold', lossStreak: coldLossStreak(bet), windowLossRate: coldWindowLossRate(bet) };
      const prev = coldByUser.get(id);
      if (!prev || (entry.lossStreak || 0) > (prev.lossStreak || 0)) coldByUser.set(id, entry);
    }

    if (isWhaleBet(bet, config, median)) {
      const entry: HeatEntry = { bet, kind: 'whale', stakeMultiple: whaleStakeMultiple(bet, median) };
      const prev = whaleByUser.get(id);
      if (!prev || betAmount(entry.bet) > betAmount(prev.bet)) whaleByUser.set(id, entry);
    }
  }

  const hot = [...hotByUser.values()].sort((a, b) => (b.streak || 0) - (a.streak || 0) || (b.windowWinRate || 0) - (a.windowWinRate || 0)).slice(0, limitPerLane);
  const whales = [...whaleByUser.values()].sort((a, b) => betAmount(b.bet) - betAmount(a.bet)).slice(0, limitPerLane);
  const cold = [...coldByUser.values()].sort((a, b) => (b.lossStreak || 0) - (a.lossStreak || 0) || (b.windowLossRate || 0) - (a.windowLossRate || 0)).slice(0, limitPerLane);
  return { hot, whales, cold };
}
